/*
 * IGIOO lens selector.
 * Steps: usage → prescription → lens type → thickness → coating → review.
 * Adds the frame and the lens component variants (lens-vision / lens-type / lens-thickness / lens-coating)
 * to the cart in one /cart/add.js request. Prescription files are uploaded to Shopify first
 * (line item file upload) so the order gets a cdn.shopify.com link.
 */
(() => {
  if (customElements.get('igioo-lens-selector')) return;

  const STORAGE_KEY = 'igioo-rx';

  const ICONS = {
    distance: '<path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z"/><circle cx="12" cy="12" r="2.5"/>',
    reading: '<path d="M4 5h6a2 2 0 0 1 2 2v12a2 2 0 0 0-2-2H4zM20 5h-6a2 2 0 0 0-2 2v12a2 2 0 0 1 2-2h6z"/>',
    computer: '<rect x="3" y="4" width="18" height="12" rx="1.5"/><path d="M8 20h8M12 16v4"/>',
    progressive: '<path d="M6 4c0 6 12 10 12 16M6 20C6 14 18 10 18 4"/>',
    bifocal: '<circle cx="12" cy="12" r="8"/><path d="M8 15h8"/>',
    readers: '<circle cx="11" cy="11" r="6"/><path d="m20 20-4.5-4.5M11 8v6M8 11h6"/>',
    plain: '<circle cx="7" cy="13" r="4"/><circle cx="17" cy="13" r="4"/><path d="M11 13h2"/>',
    frame: '<path d="M3 13h18M5 13l1-5h3M19 13l-1-5h-3"/><circle cx="7" cy="15" r="3"/><circle cx="17" cy="15" r="3"/>',
    clear: '<circle cx="12" cy="12" r="8"/>',
    blue: '<rect x="4" y="5" width="16" height="11" rx="1.5"/><path d="M9 20h6M7 10l3 2 7-4"/>',
    photo: '<circle cx="12" cy="12" r="4"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4"/>',
    driving: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="2"/><path d="M4.5 10h5M14.5 10h5M12 14v6"/>',
    sun: '<circle cx="12" cy="12" r="4" fill="currentColor"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2"/>',
    upload: '<path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/>',
    edit: '<path d="M4 20h4L19 9l-4-4L4 16z"/>',
    later: '<circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/>',
    check: '<path d="m5 12 5 5 9-10"/>',
    shield: '<path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z"/><path d="m9 12 2 2 4-4"/>',
    truck: '<path d="M3 6h11v10H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="17" r="1.8"/><circle cx="17" cy="17" r="1.8"/>',
    back: '<path d="m15 18-6-6 6-6"/>',
    close: '<path d="M6 6l12 12M18 6 6 18"/>'
  };
  const icon = (name) =>
    `<svg class="ils-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${ICONS[name] || ''}</svg>`;

  // Vision options. `variant` must match a variant title of the lens-vision product.
  const USAGES = [
    { key: 'single', icon: 'distance', title: 'Single vision', text: 'Everyday and distance wear – corrects near- or farsightedness.', rx: true, badge: 'Most popular' },
    { key: 'progressive', icon: 'progressive', title: 'Progressive', text: 'Near, middle and far in one lens, without a visible line.', rx: true, add: true },
    { key: 'reading', icon: 'reading', title: 'Reading (prescription)', text: 'Sharp vision up close, about 30 cm.', rx: true },
    { key: 'computer', icon: 'computer', title: 'Computer', text: 'Screens and desk work, 50–70 cm.', rx: true },
    { key: 'bifocal', icon: 'bifocal', title: 'Bifocal', text: 'Distance and reading, with a visible reading segment.', rx: true, add: true },
    { key: 'readers', icon: 'readers', title: 'Readers', text: 'Simple magnification – no prescription needed.', readers: true },
    { key: 'plain', icon: 'plain', title: 'Non-prescription', text: 'Fashion, blue light or sun lenses without correction.' },
    { key: 'frame', icon: 'frame', title: 'Frame only', text: 'No lenses – just the frame.' }
  ];

  // Lens types shown to the customer. Each maps to one of the store's lens products (see lensSource).
  const LENSES = [
    { key: 'clear', icon: 'clear', title: 'Clear', text: 'Crystal-clear lenses for every day.' },
    { key: 'blue', icon: 'blue', title: 'Blue light filter', text: 'Less glare and eye strain from screens.', badge: 'Popular' },
    { key: 'blueheavy', icon: 'blue', title: 'Blue light – heavy screen use', text: 'Stronger filter for long days at the screen.' },
    { key: 'blue360', icon: 'blue', title: 'Blue light 360', text: 'Our strongest blue light protection.' },
    { key: 'photo', icon: 'photo', title: 'Light-adaptive', text: 'Clear indoors, darkens automatically in sunlight.' },
    { key: 'photoblue', icon: 'photo', title: 'Light-adaptive + blue light', text: 'Darkens outdoors and filters screen light indoors.' },
    { key: 'driving', icon: 'driving', title: 'Driving', text: 'Reduces glare from headlights and wet roads.' },
    { key: 'tinted', icon: 'sun', title: 'Sun – tinted', text: 'Classic sun tint with UV400 protection.', sun: true },
    { key: 'polarized', icon: 'sun', title: 'Sun – polarized', text: 'Cuts reflections from water, snow and roads.', sun: true, badge: 'Best outdoors' },
    { key: 'mirrored', icon: 'sun', title: 'Sun – mirrored', text: 'Reflective finish with UV400 protection.', sun: true },
    { key: 'gradient', icon: 'sun', title: 'Sun – gradient', text: 'Darker at the top, lighter at the bottom. UV400 protection.', sun: true },
    { key: 'relax', icon: 'computer', title: 'Anti-fatigue', text: 'A small reading boost at the bottom of the lens to relax your eyes on long screen days.' }
  ];

  // Optional extras, sold as variants of the "Lens Add-ons" product (option "Add-on").
  const EXTRAS_INFO = {
    'Anti-Fog': { title: 'Anti-fog', text: 'Stops lenses fogging up – great with masks, sport and cold weather.' },
    'Water-repellent': { title: 'Water-repellent', text: 'Rain and splashes roll off, and lenses stay clean for longer.' }
  };

  // Title prefix of the lens products per usage (e.g. "Progressive-Clear").
  const PREFIX = { single: 'Distance', reading: 'Reading', computer: 'Intermediate', progressive: 'Progressive', bifocal: 'Bifocal', readers: 'Readers' };

  const TINT_COLORS = { gray: '#6b6f73', brown: '#7a5433', green: '#4d6b4f', blue: '#3a5f8f', orange: '#d9822b', yellow: '#e2c23b', silver: '#c9ced3', pink: '#d99aa8', purple: '#7b5ea7', purpule: '#7b5ea7', 'amber rose': '#c98a6b' };
  const tintLabel = (value) => (/^purpule$/i.test(value) ? 'Purple' : value);

  const THICKNESS_INFO = {
    '1.56': { title: 'Standard 1.56', text: 'Great for mild prescriptions (up to ±2.00).' },
    '1.61': { title: 'Thin 1.61', text: 'About 25% thinner and lighter. Ideal up to ±4.00.' },
    '1.67': { title: 'Extra thin 1.67', text: 'About 40% thinner – best for strong prescriptions.' }
  };
  const COATING_INFO = {
    'Standard Coating': { title: 'Standard', text: 'Scratch-resistant and anti-reflective.' },
    'Advanced Coating': { title: 'Advanced', text: 'Adds 100% UV protection.', badge: 'Recommended' },
    'Ultimate Coating': { title: 'Ultimate', text: 'Adds dust- and water-repellent, easy-clean layers.' }
  };
  const lensIndex = (value) => (String(value).match(/1\.\d\d/) || [])[0];
  const isBlueBundle = (value) => /blue light/i.test(value);

  const range = (from, to, step) => {
    const values = [];
    for (let v = from; v <= to + 1e-9; v += step) values.push(Math.round(v * 100) / 100);
    return values;
  };
  const signed = (v) => (v > 0 ? '+' : v < 0 ? '-' : '') + Math.abs(v).toFixed(2);

  class IgiooLensSelector extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-lens-config]').textContent);
      this.money = new Intl.NumberFormat(this.config.locale || undefined, { style: 'currency', currency: this.config.currency });
      this.querySelector('[data-lens-open]').addEventListener('click', () => this.open());
      const preload = () => this.loadCatalog();
      if ('requestIdleCallback' in window) requestIdleCallback(preload, { timeout: 3000 }); else setTimeout(preload, 1500);
    }

    // Loads the store's lens products (prices in the customer's currency) once per page.
    loadCatalog() {
      if (!this.catalogPromise) {
        this.catalogPromise = Promise.all(this.config.lensHandles.map((handle) =>
          fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
        )).then((products) => {
          this.catalog = {};
          products.filter(Boolean).forEach((product) => { if (!this.catalog[product.title]) this.catalog[product.title] = product; });
        });
      }
      return this.catalogPromise;
    }

    // ---------- state ----------

    reset() {
      const saved = this.loadSaved();
      this.state = {
        step: 'usage',
        usage: null,
        rxMethod: null,
        rx: saved || this.emptyRx(),
        rxSaved: Boolean(saved),
        saveRx: true,
        file: null,
        fileUrl: null,
        magnification: 1.5,
        lens: null,
        tint: null,
        thickness: null,
        coating: null,
        extras: [],
        error: ''
      };
    }

    emptyRx() {
      const eye = { sph: 0, cyl: 0, axis: '', add: '' };
      return { od: { ...eye }, os: { ...eye }, pdType: 'single', pd: 63, pdRight: 31.5, pdLeft: 31.5 };
    }

    loadSaved() {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
    }

    frameVariantId() {
      // Concept keeps the variant input inside the form; Focal links it with a form="" attribute.
      const formId = this.dataset.formId;
      const input = document.querySelector(`#${formId} [name="id"], [name="id"][form="${formId}"]`);
      return input ? String(input.value) : Object.keys(this.config.frame.variants)[0];
    }

    usage() { return USAGES.find((u) => u.key === this.state.usage); }
    lens() { return LENSES.find((l) => l.key === this.state.lens); }
    // The lens product (and fixed option values) behind a usage + lens type, or null if the store doesn't offer it.
    lensSource(usageKey, lensKey, coating) {
      const catalog = this.catalog || {};
      const sunFrame = this.config.frameKind === 'sunglasses';
      let title;
      if (usageKey === 'plain') {
        title = {
          clear: `Non_Prescription_Clear-${coating || 'Standard Coating'}`,
          blue: 'Non_Prescription_BLB-Blue Blocker (Daily Use)',
          blueheavy: 'Non_Prescription_BLB-Blue Blocker (Heavy Screen Use)',
          blue360: 'Non_Prescription_BLB-Blue 360',
          photo: 'Non_Prescription_Sun-Photochromic',
          photoblue: 'Non_Prescription_Sun-Photochromic',
          tinted: 'Non_Prescription_Sun-Tinted',
          mirrored: 'Non_Prescription_Sun-Mirrored',
          polarized: 'Non_Prescription_Sun-Polarized',
          gradient: 'Non_Prescription_Sun-Gradient'
        }[lensKey];
      } else {
        const prefix = PREFIX[usageKey];
        if (!prefix) return null;
        const single = usageKey === 'single';
        title = {
          clear: `${prefix}-Clear`,
          blue: `${prefix}-Blue Light Blocking`,
          blue360: `${prefix}-Blue Light Blocking`,
          photo: single && sunFrame ? 'Sun-Photochromic' : `${prefix}-Photochromic`,
          photoblue: single && sunFrame ? 'Sun-Photochromic' : `${prefix}-Photochromic`,
          driving: single ? 'Distance-Driving lense' : null,
          tinted: single ? 'Sun-Tinted' : null,
          mirrored: single ? 'Sun-Mirrored' : null,
          polarized: single ? 'Sun-Polarized' : null,
          gradient: single ? 'Sun-Gradient' : null,
          relax: single ? 'Distance-Anti-Fatigue' : null
        }[lensKey];
      }
      const product = title && catalog[title];
      if (!product) return null;
      const values = (name) => ((product.options.find((o) => o.name === name) || {}).values || []);
      const fixed = {};
      // Non-prescription blue light lenses are separate products; the others pick a "Lens Types" value.
      if (usageKey !== 'plain' && (lensKey === 'blue' || lensKey === 'blue360')) {
        const types = values('Lens Types');
        if (types.length) {
          const wanted = types.find((v) => (lensKey === 'blue360' ? /360/.test(v) : /daily/i.test(v)));
          if (!wanted) return null;
          fixed['Lens Types'] = wanted;
        } else if (lensKey === 'blue360') {
          return null;
        }
      }
      if (lensKey === 'photo' || lensKey === 'photoblue') {
        const bundles = values('Lense Bundle');
        if (bundles.length) {
          const wanted = bundles.find((v) => (lensKey === 'photoblue' ? isBlueBundle(v) : !isBlueBundle(v)));
          if (!wanted) return null;
          fixed['Lense Bundle'] = wanted;
        } else if (lensKey === 'photoblue') {
          return null;
        }
      }
      return { product, fixed };
    }

    source() {
      return this.state.usage && this.state.lens ? this.lensSource(this.state.usage, this.state.lens, this.state.coating) : null;
    }

    matchVariants(source, choices = {}) {
      if (!source) return [];
      const wanted = { ...source.fixed, ...choices };
      return source.product.variants.filter((variant) => source.product.options.every((option, i) =>
        wanted[option.name] == null || variant.options[i] === wanted[option.name]));
    }

    cheapest(variants) {
      return variants.reduce((best, v) => (!best || v.price < best.price ? v : best), null);
    }

    // Cheapest price for a usage + lens type (plain clear: over its three coating products).
    fromPrice(usageKey, lensKey) {
      const coatings = usageKey === 'plain' && lensKey === 'clear' ? Object.keys(COATING_INFO) : [null];
      const prices = coatings.map((c) => this.cheapest(this.matchVariants(this.lensSource(usageKey, lensKey, c))))
        .filter(Boolean).map((v) => v.price);
      return prices.length ? Math.min(...prices) : null;
    }

    choices() {
      const s = this.state;
      return { 'Lense Bundle': s.thickness, Coatings: s.coating, 'Tint Color': s.tint };
    }

    optionValues(name) {
      const src = this.source();
      if (!src || src.fixed[name]) return [];
      const option = src.product.options.find((o) => o.name === name);
      return option ? option.values : [];
    }

    thicknessOptions() {
      return this.optionValues('Lense Bundle').filter((v) => !isBlueBundle(v) && THICKNESS_INFO[lensIndex(v)]);
    }

    coatingOptions() {
      if (this.state.usage === 'plain' && this.state.lens === 'clear') {
        return Object.keys(COATING_INFO).filter((c) => this.lensSource('plain', 'clear', c));
      }
      return this.optionValues('Coatings');
    }

    tintOptions() {
      return this.optionValues('Tint Color');
    }

    // Add-on variants ({ value, variant }) offered after the coating step.
    extraOptions() {
      const product = (this.catalog || {})['Lens Add-ons'];
      if (!product) return [];
      return product.variants.filter((v) => EXTRAS_INFO[v.options[0]]).map((v) => ({ value: v.options[0], variant: v }));
    }

    selectedExtras() {
      return this.extraOptions().filter((e) => this.state.extras.includes(e.value));
    }

    extrasPrice() {
      return this.selectedExtras().reduce((sum, e) => sum + e.variant.price, 0);
    }

    // Price of the selected lens (cheapest match while some options are still open), in cents.
    lensVariant() {
      return this.cheapest(this.matchVariants(this.source(), this.choices()));
    }

    // Extra cost of `value` for `optionName` compared with the cheapest value, given the other choices.
    optionDelta(optionName, value, allValues) {
      const priceFor = (v) => {
        if (optionName === 'Coatings' && this.state.usage === 'plain' && this.state.lens === 'clear') {
          const variant = this.cheapest(this.matchVariants(this.lensSource('plain', 'clear', v)));
          return variant ? variant.price : null;
        }
        const variant = this.cheapest(this.matchVariants(this.source(), { ...this.choices(), [optionName]: v }));
        return variant ? variant.price : null;
      };
      const prices = allValues.map(priceFor).filter((p) => p !== null);
      const own = priceFor(value);
      return own === null || !prices.length ? null : own - Math.min(...prices);
    }

    availableUsages() {
      const usages = this.config.frameKind === 'sunglasses' ? USAGES.filter((u) => ['single', 'plain', 'frame'].includes(u.key)) : USAGES;
      return usages.filter((u) => u.key === 'frame' || this.lensesFor(u.key).length);
    }

    lensesFor(usageKey) {
      const sunglasses = this.config.frameKind === 'sunglasses';
      return LENSES.filter((l) => {
        if (sunglasses && !(l.sun || l.key === 'photo' || l.key === 'photoblue')) return false;
        return this.fromPrice(usageKey, l.key) !== null;
      });
    }

    availableLenses() {
      return this.lensesFor(this.state.usage);
    }

    steps() {
      const usage = this.usage();
      if (!usage || usage.key === 'frame') return ['usage', 'review'];
      const steps = ['usage'];
      if (usage.rx || usage.readers) steps.push('prescription');
      steps.push('lens');
      if (this.state.lens) {
        if (this.tintOptions().length > 1) steps.push('tint');
        if (this.thicknessOptions().length > 1) steps.push('thickness');
        if (this.coatingOptions().length > 1) steps.push('coating');
        if (this.extraOptions().length) steps.push('extras');
      }
      steps.push('review');
      return steps;
    }

    strength() {
      const { od, os } = this.state.rx;
      if (this.state.rxMethod !== 'manual') return null;
      return Math.max(Math.abs(od.sph) + Math.abs(od.cyl), Math.abs(os.sph) + Math.abs(os.cyl));
    }

    // Recommended lens index for the entered prescription (Thin 1.61 when unknown).
    recommendedIndex() {
      const s = this.strength();
      if (s === null) return '1.61';
      if (s > 4) return '1.67';
      if (s > 2) return '1.61';
      return '1.56';
    }

    recommendedThickness() {
      const options = this.thicknessOptions();
      return options.find((v) => lensIndex(v) === this.recommendedIndex()) || options[0] || null;
    }

    lensSummary() {
      const usage = this.usage();
      const lens = this.lens();
      if (!usage || usage.key === 'frame' || !lens) return '';
      const s = this.state;
      return [
        usage.title,
        lens.title,
        s.thickness && THICKNESS_INFO[lensIndex(s.thickness)] ? THICKNESS_INFO[lensIndex(s.thickness)].title : '',
        s.coating && COATING_INFO[s.coating] ? `${COATING_INFO[s.coating].title} coating` : '',
        s.tint ? `${tintLabel(s.tint)} tint` : '',
        ...this.selectedExtras().map((e) => EXTRAS_INFO[e.value].title)
      ].filter(Boolean).join(' · ');
    }

    lensPrice() {
      const variant = this.lensVariant();
      return variant ? variant.price + this.extrasPrice() : 0;
    }

    framePrice() {
      const variant = this.config.frame.variants[this.frameVariantId()];
      return variant ? variant.price : 0;
    }

    total() {
      return this.framePrice() + (this.usage() && this.usage().key !== 'frame' ? this.lensPrice() : 0);
    }

    // ---------- rendering ----------

    async open() {
      const button = this.querySelector('[data-lens-open]');
      if (!this.catalog) {
        button.setAttribute('aria-busy', 'true');
        await this.loadCatalog();
        button.removeAttribute('aria-busy');
      }
      this.reset();
      if (!this.dialog) this.buildDialog();
      this.render();
      this.dialog.showModal();
      document.documentElement.classList.add('ils-lock');
    }

    close() {
      this.dialog.close();
    }

    buildDialog() {
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'ils';
      this.dialog.setAttribute('aria-label', 'Choose your lenses');
      this.dialog.innerHTML = `
        <div class="ils-shell">
          <aside class="ils-aside">
            <img class="ils-frame-img" alt="" width="600" height="300">
            <p class="ils-frame-title"></p>
            <div class="ils-summary"></div>
            <ul class="ils-trust">
              <li>${icon('shield')} 14-day fit &amp; style guarantee</li>
              <li>${icon('check')} 1-year warranty</li>
              <li>${icon('truck')} Free shipping over ${this.money.format(99)}</li>
            </ul>
          </aside>
          <section class="ils-main">
            <header class="ils-header">
              <button type="button" class="ils-icon-btn" data-back aria-label="Back">${icon('back')}</button>
              <div class="ils-progress"><span></span></div>
              <button type="button" class="ils-icon-btn" data-close aria-label="Close">${icon('close')}</button>
            </header>
            <div class="ils-body" tabindex="-1"></div>
            <footer class="ils-footer">
              <div class="ils-total"><span>Total</span><strong></strong></div>
              <button type="button" class="button button--primary ils-next" data-next></button>
            </footer>
          </section>
        </div>`;
      document.body.appendChild(this.dialog);
      this.body = this.dialog.querySelector('.ils-body');
      this.dialog.addEventListener('click', (e) => this.onClick(e));
      this.dialog.addEventListener('change', (e) => this.onChange(e));
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('ils-lock'));
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
    }

    render() {
      const s = this.state;
      const steps = this.steps();
      const index = steps.indexOf(s.step);
      const frame = this.config.frame.variants[this.frameVariantId()] || {};
      this.dialog.querySelector('.ils-frame-img').src = frame.image || this.config.frame.image || '';
      this.dialog.querySelector('.ils-frame-title').textContent =
        frame.title && frame.title !== 'Default Title' ? `${this.config.frame.title} – ${frame.title}` : this.config.frame.title;
      this.dialog.querySelector('.ils-progress span').style.width = `${((index + 1) / steps.length) * 100}%`;
      this.dialog.querySelector('[data-back]').style.visibility = index > 0 ? 'visible' : 'hidden';
      this.dialog.querySelector('.ils-total strong').textContent = this.money.format(this.total() / 100);
      this.dialog.querySelector('.ils-summary').innerHTML = this.summaryHtml();

      const renderers = {
        usage: () => this.renderUsage(),
        prescription: () => this.renderPrescription(),
        lens: () => this.renderLens(),
        tint: () => this.renderTint(),
        thickness: () => this.renderThickness(),
        coating: () => this.renderCoating(),
        extras: () => this.renderExtras(),
        review: () => this.renderReview()
      };
      this.body.innerHTML = renderers[s.step]() + (s.error ? `<p class="ils-error" role="alert">${s.error}</p>` : '');

      const next = this.dialog.querySelector('[data-next]');
      next.textContent = s.step === 'review' ? 'Add to cart' : 'Continue';
      next.disabled = !this.canContinue();
      this.body.scrollTop = 0;
    }

    heading(title, text) {
      return `<h2 class="ils-title">${title}</h2>${text ? `<p class="ils-lead">${text}</p>` : ''}`;
    }

    priceTag(cents, included = 'Included') {
      if (cents === undefined || cents === null) return '';
      return `<span class="ils-price">${cents > 0 ? '+' + this.money.format(cents / 100) : included}</span>`;
    }

    card({ name, value, checked, iconName, title, text, price, badge, extra = '', type = 'radio' }) {
      return `
        <label class="ils-card${checked ? ' is-checked' : ''}">
          <input type="${type}" name="${name}" value="${value}" ${checked ? 'checked' : ''}>
          ${iconName ? `<span class="ils-card-icon">${icon(iconName)}</span>` : ''}
          <span class="ils-card-body">
            <span class="ils-card-title">${title}${badge ? ` <em class="ils-badge">${badge}</em>` : ''}</span>
            ${text ? `<span class="ils-card-text">${text}</span>` : ''}
            ${price || ''}
            ${extra}
          </span>
        </label>`;
    }

    renderUsage() {
      const cards = this.availableUsages().map((u) => {
        const prices = u.key === 'frame' ? [] : this.lensesFor(u.key).map((l) => this.fromPrice(u.key, l.key));
        return this.card({
          name: 'usage', value: u.key, checked: this.state.usage === u.key, iconName: u.icon,
          title: u.title, text: u.text, badge: u.badge,
          price: prices.length ? `<span class="ils-price">lenses from ${this.money.format(Math.min(...prices) / 100)}</span>` : ''
        });
      });
      return this.heading('What will you use your glasses for?', 'Choose the lens that fits your day. You can change everything before checkout.') +
        `<div class="ils-cards">${cards.join('')}</div>`;
    }

    renderPrescription() {
      const usage = this.usage();
      if (usage.readers) {
        const chips = range(1, 3.5, 0.25).map((v) => `
          <label class="ils-chip${this.state.magnification === v ? ' is-checked' : ''}">
            <input type="radio" name="magnification" value="${v}" ${this.state.magnification === v ? 'checked' : ''}>+${v.toFixed(2)}
          </label>`).join('');
        return this.heading('Choose your magnification', 'Not sure? Most people start between +1.00 and +2.00.') +
          `<div class="ils-chips">${chips}</div>`;
      }
      const s = this.state;
      const methods = [
        { key: 'manual', icon: 'edit', title: 'Enter it now', text: 'Takes about a minute – copy the values from your prescription.', badge: 'Fastest' },
        { key: 'upload', icon: 'upload', title: 'Upload a photo or PDF', text: 'We read it for you and check every value.' },
        { key: 'later', icon: 'later', title: 'Send it later', text: 'Order now and email it to us – we start once it arrives.' }
      ];
      let html = this.heading('Add your prescription', 'Your prescription stays private and is only used to make your lenses.');
      if (s.rxSaved && s.rxMethod === 'manual') html += `<p class="ils-note">${icon('check')} We filled in the prescription you saved on this device.</p>`;
      html += `<div class="ils-cards ils-cards--compact">${methods.map((m) => this.card({
        name: 'rxMethod', value: m.key, checked: s.rxMethod === m.key, iconName: m.icon, title: m.title, text: m.text, badge: m.badge
      })).join('')}</div>`;
      if (s.rxMethod === 'manual') html += this.rxFormHtml();
      if (s.rxMethod === 'upload') html += this.uploadHtml();
      if (s.rxMethod === 'upload' || s.rxMethod === 'later') html += this.pdHtml();
      return html;
    }

    rxFormHtml() {
      const { rx } = this.state;
      const usage = this.usage();
      const opts = (values, selected, format) => values.map((v) =>
        `<option value="${v}" ${String(v) === String(selected) ? 'selected' : ''}>${format(v)}</option>`).join('');
      const eyeRow = (eye, label) => `
        <tr>
          <th scope="row">${label}</th>
          <td><select name="${eye}.sph" aria-label="${label} SPH">${opts(range(-16, 12, 0.25), rx[eye].sph, signed)}</select></td>
          <td><select name="${eye}.cyl" aria-label="${label} CYL">${opts(range(-6, 6, 0.25), rx[eye].cyl, signed)}</select></td>
          <td><select name="${eye}.axis" aria-label="${label} AXIS" ${Number(rx[eye].cyl) === 0 ? 'disabled' : ''}>
            <option value="">–</option>${opts(range(1, 180, 1), rx[eye].axis, (v) => v)}</select></td>
          ${usage.add ? `<td><select name="${eye}.add" aria-label="${label} ADD"><option value="">–</option>${opts(range(0.75, 3.5, 0.25), rx[eye].add, (v) => '+' + v.toFixed(2))}</select></td>` : ''}
        </tr>`;
      return `
        <div class="ils-rx">
          <table class="ils-rx-table">
            <thead><tr><th></th><th>SPH</th><th>CYL</th><th>AXIS</th>${usage.add ? '<th>ADD</th>' : ''}</tr></thead>
            <tbody>${eyeRow('od', 'Right (OD)')}${eyeRow('os', 'Left (OS)')}</tbody>
          </table>
          <details class="ils-help"><summary>How to read your prescription</summary>
            <p><b>OD</b> is your right eye, <b>OS</b> your left. <b>SPH</b> (sphere) is the main strength, <b>CYL</b> and <b>AXIS</b> correct astigmatism – leave them empty if your prescription has none. <b>ADD</b> is the extra reading power for progressive and bifocal lenses.</p>
          </details>
          ${this.pdHtml()}
          <label class="ils-check"><input type="checkbox" name="saveRx" ${this.state.saveRx ? 'checked' : ''}> Save this prescription on this device for next time</label>
        </div>`;
    }

    pdHtml() {
      const { rx } = this.state;
      const opts = (values, selected) => values.map((v) => `<option value="${v}" ${Number(selected) === v ? 'selected' : ''}>${v}</option>`).join('');
      return `
        <fieldset class="ils-pd">
          <legend>Pupillary distance (PD)</legend>
          <div class="ils-segment">
            <label class="${rx.pdType === 'single' ? 'is-checked' : ''}"><input type="radio" name="pdType" value="single" ${rx.pdType === 'single' ? 'checked' : ''}>One number</label>
            <label class="${rx.pdType === 'dual' ? 'is-checked' : ''}"><input type="radio" name="pdType" value="dual" ${rx.pdType === 'dual' ? 'checked' : ''}>Two numbers</label>
            <label class="${rx.pdType === 'unknown' ? 'is-checked' : ''}"><input type="radio" name="pdType" value="unknown" ${rx.pdType === 'unknown' ? 'checked' : ''}>I don't know</label>
          </div>
          ${rx.pdType === 'single' ? `<select name="pd" aria-label="PD">${opts(range(50, 80, 0.5), rx.pd)}</select>` : ''}
          ${rx.pdType === 'dual' ? `<div class="ils-pd-dual"><label>Right <select name="pdRight">${opts(range(25, 40, 0.5), rx.pdRight)}</select></label><label>Left <select name="pdLeft">${opts(range(25, 40, 0.5), rx.pdLeft)}</select></label></div>` : ''}
          ${rx.pdType === 'unknown' ? '<p class="ils-note">No problem – we use the average adult PD (63) or contact you to measure it together.</p>' : ''}
        </fieldset>`;
    }

    uploadHtml() {
      const { file, fileUrl } = this.state;
      return `
        <label class="ils-upload${file ? ' has-file' : ''}">
          <input type="file" name="rxFile" accept="image/*,application/pdf">
          ${icon('upload')}
          <span>${file ? `<b>${file.name}</b><br>${fileUrl ? 'Uploaded securely' : 'Uploading…'} · tap to change` : '<b>Choose a photo or PDF</b><br>JPG, PNG, HEIC or PDF, up to 10 MB'}</span>
        </label>`;
    }

    renderLens() {
      const cards = this.availableLenses().map((l) => this.card({
        name: 'lens', value: l.key, checked: this.state.lens === l.key, iconName: l.icon, title: l.title, text: l.text, badge: l.badge,
        price: `<span class="ils-price">from ${this.money.format(this.fromPrice(this.state.usage, l.key) / 100)}</span>`
      }));
      return this.heading('Choose your lens type', 'All lenses include a scratch-resistant, anti-reflective finish.') +
        `<div class="ils-cards">${cards.join('')}</div>`;
    }

    renderTint() {
      const swatches = this.tintOptions().map((t) => `
        <label class="ils-swatch${this.state.tint === t ? ' is-checked' : ''}">
          <input type="radio" name="tint" value="${t}" ${this.state.tint === t ? 'checked' : ''}>
          <span style="background:${TINT_COLORS[t.toLowerCase()] || '#999'}"></span>${tintLabel(t)}
        </label>`).join('');
      return this.heading('Pick a tint colour', this.lens().title) + `<div class="ils-swatches">${swatches}</div>`;
    }

    renderThickness() {
      const recommended = this.recommendedThickness();
      const options = this.thicknessOptions();
      const cards = options.map((value) => {
        const info = THICKNESS_INFO[lensIndex(value)];
        return this.card({
          name: 'thickness', value, checked: this.state.thickness === value, title: info.title, text: info.text,
          badge: value === recommended ? (this.strength() === null ? 'Suits most' : 'Recommended for you') : '',
          price: this.priceTag(this.optionDelta('Lense Bundle', value, options))
        });
      });
      return this.heading('Choose lens thickness', 'Thinner lenses look better and weigh less, especially with stronger prescriptions.') +
        `<div class="ils-cards">${cards.join('')}</div>`;
    }

    renderCoating() {
      const options = this.coatingOptions();
      const cards = options.map((value) => {
        const info = COATING_INFO[value] || { title: value, text: '' };
        return this.card({
          name: 'coating', value, checked: this.state.coating === value, title: info.title, text: info.text, badge: info.badge,
          price: this.priceTag(this.optionDelta('Coatings', value, options))
        });
      });
      return this.heading('Add a coating', 'Anti-reflective and scratch-resistant coatings are always included.') +
        `<div class="ils-cards">${cards.join('')}</div>`;
    }

    renderExtras() {
      const cards = this.extraOptions().map((e) => this.card({
        name: 'extra', value: e.value, type: 'checkbox', checked: this.state.extras.includes(e.value),
        title: EXTRAS_INFO[e.value].title, text: EXTRAS_INFO[e.value].text,
        price: this.priceTag(e.variant.price)
      }));
      return this.heading('Any extras?', 'Optional – skip this step if you don\'t need them.') +
        `<div class="ils-cards">${cards.join('')}</div>`;
    }

    renderReview() {
      const usage = this.usage();
      const s = this.state;
      const rows = [[this.config.frame.title, this.framePrice(), null]];
      if (usage.key !== 'frame') {
        rows.push([usage.title, null, 'usage'], [this.lens().title, null, 'lens']);
        if (s.tint) rows.push([`Tint: ${tintLabel(s.tint)}`, null, 'tint']);
        if (s.thickness && THICKNESS_INFO[lensIndex(s.thickness)]) rows.push([THICKNESS_INFO[lensIndex(s.thickness)].title, null, 'thickness']);
        if (s.coating && COATING_INFO[s.coating] && this.steps().includes('coating')) rows.push([`${COATING_INFO[s.coating].title} coating`, null, 'coating']);
        this.selectedExtras().forEach((e) => rows.push([EXTRAS_INFO[e.value].title, null, 'extras']));
        const rxLine = this.rxSummary();
        if (rxLine) rows.push([rxLine, null, 'prescription']);
        rows.push(['Lenses', this.lensPrice(), null]);
      }
      const list = rows.map(([label, price, step]) => `
        <li><span>${label}${step ? ` <button type="button" class="ils-link" data-goto="${step}">Edit</button>` : ''}</span>
        <span>${price === null ? '' : this.money.format(price / 100)}</span></li>`).join('');
      return this.heading(usage.key === 'frame' ? 'Frame only' : 'Review your glasses', 'Made for you and checked by our opticians before shipping.') +
        `<ul class="ils-review">${list}</ul>
         <div class="ils-review-total"><span>Total</span><strong>${this.money.format(this.total() / 100)}</strong></div>
         <ul class="ils-trust ils-trust--inline">
           <li>${icon('shield')} 14-day fit &amp; style guarantee</li>
           <li>${icon('check')} 1-year warranty</li>
           <li>${icon('truck')} Ships in 2–8 days</li>
         </ul>`;
    }

    summaryHtml() {
      const summary = this.lensSummary();
      if (!summary) return '';
      return `<ul><li><span>${summary}</span><span>${this.money.format(this.lensPrice() / 100)}</span></li></ul>`;
    }

    rxSummary() {
      const s = this.state;
      const usage = this.usage();
      if (!usage) return '';
      if (usage.readers) return `Magnification +${Number(s.magnification).toFixed(2)}`;
      if (!usage.rx) return '';
      return { manual: 'Prescription entered', upload: 'Prescription uploaded', later: 'Prescription: send later' }[s.rxMethod] || '';
    }

    // ---------- interaction ----------

    canContinue() {
      const s = this.state;
      switch (s.step) {
        case 'usage': return Boolean(s.usage);
        case 'prescription':
          if (this.usage().readers) return Boolean(s.magnification);
          if (s.rxMethod === 'upload') return Boolean(s.fileUrl);
          return Boolean(s.rxMethod);
        case 'lens': return Boolean(s.lens);
        case 'tint': return Boolean(s.tint);
        case 'thickness': return Boolean(s.thickness);
        case 'coating': return Boolean(s.coating);
        case 'extras': return true;
        default: return !this.busy;
      }
    }

    validateRx() {
      if (this.state.rxMethod !== 'manual') return '';
      const { od, os } = this.state.rx;
      for (const [eye, label] of [[od, 'right'], [os, 'left']]) {
        if (Number(eye.cyl) !== 0 && !eye.axis) return `Please add the AXIS for your ${label} eye (needed when CYL is not 0.00).`;
        if (this.usage().add && !eye.add) return `Please add the ADD value for your ${label} eye.`;
      }
      return '';
    }

    goto(step) {
      const s = this.state;
      s.step = step;
      s.error = '';
      // Preselect the recommended thickness and the Advanced coating when their step opens.
      if (step === 'thickness' && !s.thickness) s.thickness = this.recommendedThickness();
      if (step === 'coating' && !s.coating) {
        const coatings = this.coatingOptions();
        s.coating = coatings.includes('Advanced Coating') ? 'Advanced Coating' : coatings[0] || null;
      }
      this.render();
    }

    next() {
      const s = this.state;
      if (s.step === 'prescription') {
        const error = this.validateRx();
        if (error) { s.error = error; this.render(); return; }
        if (s.rxMethod === 'manual' && s.saveRx) {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s.rx)); } catch (e) { /* storage unavailable */ }
        }
      }
      if (s.step === 'review') { this.addToCart(); return; }
      const steps = this.steps();
      const nextStep = steps[steps.indexOf(s.step) + 1];
      this.goto(nextStep);
    }

    back() {
      const steps = this.steps();
      const i = steps.indexOf(this.state.step);
      if (i > 0) this.goto(steps[i - 1]);
    }

    onClick(e) {
      if (e.target.closest('[data-close]')) return this.close();
      if (e.target.closest('[data-back]')) return this.back();
      if (e.target.closest('[data-next]')) return this.next();
      const jump = e.target.closest('[data-goto]');
      if (jump) return this.goto(jump.dataset.goto);
    }

    onChange(e) {
      const { name, value, type, checked } = e.target;
      const s = this.state;
      if (name === 'rxFile') return this.onFile(e.target.files[0]);
      if (name === 'saveRx') { s.saveRx = checked; return; }
      if (name === 'extra') {
        s.extras = checked ? [...new Set([...s.extras, value])] : s.extras.filter((x) => x !== value);
        return this.render();
      }
      if (name.startsWith('od.') || name.startsWith('os.')) {
        const [eye, field] = name.split('.');
        s.rx[eye][field] = field === 'axis' || field === 'add' ? value : Number(value);
        if (field === 'cyl' && Number(value) === 0) s.rx[eye].axis = '';
        s.rxSaved = false;
        return this.render();
      }
      if (['pdType', 'pd', 'pdRight', 'pdLeft'].includes(name)) {
        s.rx[name] = name === 'pdType' ? value : Number(value);
        return this.render();
      }

      const autoAdvance = type === 'radio' && ['usage', 'lens', 'tint', 'thickness', 'coating'].includes(name);
      if (name === 'usage') {
        if (s.usage !== value) Object.assign(s, { lens: null, tint: null, thickness: null, coating: null, extras: [] });
        s.usage = value;
      } else if (name === 'lens') {
        if (s.lens !== value) Object.assign(s, { tint: null, thickness: null, coating: null, extras: [] });
        s.lens = value;
      } else if (name === 'magnification') {
        s.magnification = Number(value);
      } else {
        s[name] = value;
      }
      this.render();
      if (autoAdvance) setTimeout(() => this.next(), 180);
    }

    async onFile(file) {
      const s = this.state;
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { s.error = 'Please choose a file under 10 MB.'; return this.render(); }
      Object.assign(s, { file, fileUrl: null, error: '' });
      this.render();
      try {
        s.fileUrl = await this.uploadToShopify(file);
      } catch (err) {
        Object.assign(s, { file: null, error: 'Upload failed – please try again or choose “Send it later”.' });
      }
      this.render();
    }

    // Upload via a temporary cart line (Shopify stores line item files on its CDN), then remove that line.
    async uploadToShopify(file) {
      const form = new FormData();
      form.append('items[0][id]', this.frameVariantId());
      form.append('items[0][quantity]', '1');
      form.append('items[0][properties][_rx_upload]', file, file.name);
      const response = await fetch('/cart/add.js', { method: 'POST', body: form, headers: { Accept: 'application/json' } });
      const json = await response.json();
      const line = response.ok && json.items && json.items[0];
      const url = line && line.properties && line.properties._rx_upload;
      if (!url) throw new Error('upload failed');
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
      return url;
    }

    prescriptionProperties() {
      const s = this.state;
      const usage = this.usage();
      const props = {};
      if (usage.readers) { props.Magnification = `+${Number(s.magnification).toFixed(2)}`; return props; }
      if (!usage.rx) return props;
      const { rx } = s;
      if (s.rxMethod === 'manual') {
        const eye = (e) => [`SPH ${signed(e.sph)}`, Number(e.cyl) !== 0 ? `CYL ${signed(e.cyl)}` : '', e.axis ? `AXIS ${e.axis}` : '', usage.add && e.add ? `ADD +${Number(e.add).toFixed(2)}` : '']
          .filter(Boolean).join(' · ');
        props['Right eye (OD)'] = eye(rx.od);
        props['Left eye (OS)'] = eye(rx.os);
      } else if (s.rxMethod === 'upload') {
        props.Prescription = s.fileUrl;
      } else {
        props.Prescription = 'Customer will send it by email';
      }
      props.PD = rx.pdType === 'single' ? String(rx.pd) : rx.pdType === 'dual' ? `R ${rx.pdRight} / L ${rx.pdLeft}` : 'Unknown – contact customer';
      return props;
    }

    async addToCart() {
      if (this.busy) return;
      this.busy = true;
      const button = this.dialog.querySelector('[data-next]');
      button.disabled = true;
      button.textContent = 'Adding…';
      const bundle = String(Date.now());
      const usage = this.usage();
      const lensVariant = usage.key === 'frame' ? null : this.lensVariant();
      const summary = this.lensSummary();
      const frameVariant = this.config.frame.variants[this.frameVariantId()] || {};
      const imageUrl = (frameVariant.image || this.config.frame.image || '').replace(/^\/\//, 'https://');
      const frameProperties = lensVariant ? { _bundle: bundle, _role: 'frame', _image: imageUrl, _handle: this.config.frame.handle } : {};
      const items = [{ id: Number(this.frameVariantId()), quantity: 1, properties: frameProperties }];
      if (lensVariant) {
        const properties = { Lenses: summary, ...this.prescriptionProperties(), _bundle: bundle, _role: 'lens' };
        if (this.state.tint) properties.Tint = tintLabel(this.state.tint);
        items.push({ id: lensVariant.id, quantity: 1, properties });
        this.selectedExtras().forEach((e) => items.push({
          id: e.variant.id, quantity: 1, properties: { 'Add-on': EXTRAS_INFO[e.value].title, _bundle: bundle, _role: 'lens' }
        }));
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error((await response.json()).description || 'Could not add to cart');
        this.close();
        // Refresh and open the cart drawer (Focal: documentElement events; Concept: `open`).
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true, openMiniCart: true } }));
      } catch (err) {
        this.state.error = err.message;
        this.busy = false;
        this.render();
        return;
      }
      this.busy = false;
    }
  }

  customElements.define('igioo-lens-selector', IgiooLensSelector);
})();
